import * as React from 'react';
import { useLayout } from '../../contexts/LayoutContext';
import { ResizableDivider } from '../ResizableDivider';

export interface ContextPaneProps {
  children: React.ReactNode;
  /** Optional className added to the ResizableDivider for context-specific styling (e.g. header border bridge). */
  dividerClassName?: string;
  /** When true, hides the ResizableDivider (used when the pane is in a collapsed icon-only state). */
  hideDivider?: boolean;
}

/**
 * ContextPane - contextual navigation and filters
 *
 * Behavior (frontend-architecture.md §3, requirements):
 * - positioned immediately to the right of LeftNavPane
 * - hidden by default
 * - used by subpages (e.g., project detail pages)
 * - must not reserve width when hidden
 * - resizable with min/max constraints
 */
export function ContextPane({ children, dividerClassName, hideDivider }: ContextPaneProps) {
  const { contextPaneVisible, contextPaneWidth, setContextPaneWidth } = useLayout();
  const asideRef = React.useRef<HTMLElement>(null);
  // Tracks current width during drag without triggering React re-renders.
  const dragWidthRef = React.useRef(contextPaneWidth);

  // Keep dragWidthRef in sync when context value changes from outside (e.g. collapse).
  React.useEffect(() => {
    dragWidthRef.current = contextPaneWidth;
    if (asideRef.current) {
      asideRef.current.style.width = `${contextPaneWidth}px`;
    }
  }, [contextPaneWidth]);

  // Read constraints from CSS tokens (design-system.md: all visual decisions from tokens)
  const constraints = React.useMemo(() => {
    const root = document.documentElement;
    const minWidth = parseInt(
      getComputedStyle(root).getPropertyValue('--pane-context-width-min'),
      10
    );
    const maxWidth = parseInt(
      getComputedStyle(root).getPropertyValue('--pane-context-width-max'),
      10
    );
    return { minWidth, maxWidth };
  }, []);

  // During drag: mutate DOM directly - zero React re-renders, zero context updates.
  const handleResize = React.useCallback(
    (delta: number) => {
      const newWidth = Math.max(
        constraints.minWidth,
        Math.min(constraints.maxWidth, dragWidthRef.current + delta)
      );
      dragWidthRef.current = newWidth;
      if (asideRef.current) {
        asideRef.current.style.width = `${newWidth}px`;
      }
    },
    [constraints]
  );

  // On drag end: flush final width to context state (persists to localStorage).
  const handleResizeEnd = React.useCallback(() => {
    setContextPaneWidth(dragWidthRef.current);
  }, [setContextPaneWidth]);

  if (!contextPaneVisible) {
    return null;
  }

  return (
    <>
      <aside
        ref={asideRef}
        className="shell-contextpane"
        aria-label="Context pane"
        style={{ width: `${contextPaneWidth}px` }}
      >
        {children}
      </aside>
      {!hideDivider && (
        <ResizableDivider
          direction="vertical"
          onResize={handleResize}
          onResizeEnd={handleResizeEnd}
          ariaLabel="Resize context pane"
          className={dividerClassName}
        />
      )}
    </>
  );
}
